// ./src/components/features/EventVendors/VendorHistoryPanel/VendorHistoryPanel.tsx
//
// ROADMAP #202 — the record of what was decided about the documents asked of vendors, newest first: a table on desktop, cards below,
// Load more, and the filters the server applies. On a vendor's History tab and on the event's own page, which also holds vendors since
// taken off the event. Read only, so no row has an action: there is nothing to change on a line.
// ⓘ The filters sit inline at every width (one or two pickers, each full screen on a phone): a filter drawer would hide less than it costs.
import React, { useEffect, useMemo, useState } from "react";
import { Badge, Box, Group, Paper, Skeleton, Stack, Table, Text } from "@mantine/core";

import { EventVendorHistory, EventVendorHistoryFilter } from "@/utils/api";
import { useVendorHistory } from "@/hooks";
import { CardSkeletonLines, ContentLoadingOverlay, EmptyState, ErrorAlert, LoadMoreButton, SelectField, TableDateTime } from "@/components/generic";
import { useAppEnvironment } from "@/contexts";

import { DocumentFiles } from "../VendorDocumentParts/VendorDocumentParts";
import { formatVendorDay } from "../vendorUi";
import {
  VENDOR_HISTORY_COLOR,
  VENDOR_HISTORY_DECISIONS,
  vendorHistoryBackToReview,
  vendorHistoryChangeLines,
  vendorHistoryChecksChange,
  vendorHistoryLabel,
  vendorHistoryUnticked,
  vendorHistoryWho,
} from "../vendorHistoryWords";

const SHOW_DECISIONS = "decisions";
const SHOW_OPTIONS = [
  { value: SHOW_DECISIONS, label: "Approvals and send-backs" },
];

export interface VendorHistoryPanelProps {
  eventId: string;
  /** Only this vendor (its History tab). Absent: the whole event, and each line says which vendor. */
  eventVendorId?: string;
  /** The vendor's requests, to narrow to one. */
  requests?: Array<{ value: string; label: string }>;
  requestId?: string | null;
  onRequestChange?: (requestId: string | null) => void;
  /** Goes up to read it again: the page owns Refresh, its key and the pull. */
  reloadKey?: number;
}

/** A word list on one line: what was ticked, what was taken off. */
const Words: React.FC<{ label: string; words: string[]; color?: string }> = ({ label, words, color = "dimmed" }) =>
  words.length > 0 ? (
    <Text size="md" c={color}>
      {label}: {words.join("; ")}
    </Text>
  ) : null;

/** What the line says beyond its name: the note, the ticks, the document as it was then. */
const LineDetails: React.FC<{ line: EventVendorHistory }> = ({ line }) => {
  const change = vendorHistoryChecksChange(line);

  return (
    <Stack gap={4} style={{ minWidth: 0 }}>
      {line.note ? (
        <Text size="md" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {line.note}
        </Text>
      ) : null}
      <Words label="Ticked" words={line.checksMet ?? []} />
      <Words label="Not ticked" words={vendorHistoryUnticked(line)} />
      <Words label="Taken off" words={change.off} />
      <Words label="Added" words={change.added} />
      {vendorHistoryChangeLines(line).map((text) => (
        <Text key={text} size="md" c="dimmed" style={{ overflowWrap: "anywhere" }}>
          {text}
        </Text>
      ))}
      {vendorHistoryBackToReview(line) ? (
        <Text size="md" c="dimmed">
          It went back to review.
        </Text>
      ) : null}
      {line.documentExpiresOn ? (
        <Text size="md" c="dimmed">
          The document's expiry at the time: {formatVendorDay(line.documentExpiresOn)}
        </Text>
      ) : null}
      {line.document ? <DocumentFiles document={line.document} /> : null}
      {line.documentRemoved ? (
        <Text size="md" c="dimmed">
          Taken off the vendor's file since.
        </Text>
      ) : null}
      {line.recordedLater ? (
        <Text size="md" c="dimmed">
          Copied from the request when this record began: anything decided on it before that was not kept.
        </Text>
      ) : null}
    </Stack>
  );
};

const WhatCell: React.FC<{ line: EventVendorHistory; showVendor: boolean }> = ({ line, showVendor }) => (
  <Stack gap={6} style={{ minWidth: 0 }}>
    <Group gap="xs">
      <Badge size="md" variant="light" color={VENDOR_HISTORY_COLOR[line.kind ?? ""] ?? "gray"} style={{ whiteSpace: "normal", height: "auto" }}>
        {vendorHistoryLabel(line)}
      </Badge>
    </Group>
    <Text size="md" fw={600} style={{ overflowWrap: "anywhere" }}>
      {showVendor && line.vendorName ? `${line.vendorName} · ${line.documentName ?? ""}` : line.documentName}
    </Text>
    <LineDetails line={line} />
  </Stack>
);

const VendorHistoryPanelComponent: React.FC<VendorHistoryPanelProps> = ({ eventId, eventVendorId, requests, requestId = null, onRequestChange, reloadKey = 0 }) => {
  const { isDesktop } = useAppEnvironment();
  const [show, setShow] = useState<string | null>(null);

  const filter = useMemo<EventVendorHistoryFilter>(
    () => ({
      eventId: [eventId],
      ...(eventVendorId ? { eventVendorId: [eventVendorId] } : {}),
      ...(requestId ? { eventVendorRequirementId: [requestId] } : {}),
      ...(show === SHOW_DECISIONS ? { kind: VENDOR_HISTORY_DECISIONS } : {}),
    }),
    [eventId, eventVendorId, requestId, show]
  );
  const { lines, loading, isLoaded, loadingMore, hasMore, loadMore, refresh, lastError, clearError } = useVendorHistory(filter);
  const showVendor = !eventVendorId;

  useEffect(() => {
    if (reloadKey > 0) {
      void refresh();
    }
  }, [reloadKey, refresh]);

  const hasQueryNarrowing = !!requestId || !!show;

  // A link can name a request no longer asked for: its lines are still there to read.
  const requestOptions = useMemo(
    () => (requests && requestId && !requests.some((r) => r.value === requestId) ? [...requests, { value: requestId, label: "A request no longer asked for" }] : (requests ?? [])),
    [requests, requestId]
  );

  const header = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th style={{ width: 150 }}>When</Table.Th>
        <Table.Th>What happened</Table.Th>
        <Table.Th style={{ width: 260 }}>Who</Table.Th>
      </Table.Tr>
    </Table.Thead>
  );

  const filters = (
    <Group gap="md" align="flex-start" wrap="wrap">
      {onRequestChange && requests ? (
        <Box style={{ flex: "1 1 240px", maxWidth: 360 }}>
          <SelectField label="Document" placeholder="All of them" data={requestOptions} value={requestId} onChange={(_n, next) => onRequestChange((next as string) || null)} clearable disabled={!isLoaded} size="md" />
        </Box>
      ) : null}
      <Box style={{ flex: "1 1 240px", maxWidth: 360 }}>
        <SelectField label="Show" placeholder="Everything" data={SHOW_OPTIONS} value={show} onChange={(_n, next) => setShow((next as string) || null)} clearable disabled={!isLoaded} size="md" />
      </Box>
    </Group>
  );

  const body = (): React.ReactNode => {
    // ⚠ The FIRST read only. ▶ npm run check-list-states
    if (!isLoaded) {
      return (
        <Stack gap="xs" aria-busy="true">
          <Box visibleFrom="md">
            <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
              <Table verticalSpacing="sm" horizontalSpacing="md">
                {header}
                <Table.Tbody>
                  {[0, 1, 2].map((i) => (
                    <Table.Tr key={i}>
                      <Table.Td>
                        <Skeleton height={16} width="70%" />
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={8}>
                          <Skeleton height={22} width={120} radius="xl" />
                          <Skeleton height={16} width="50%" />
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Skeleton height={16} width="60%" />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          </Box>
          {[0, 1, 2].map((i) => (
            <Paper key={`c-${i}`} withBorder radius="md" p="md" hiddenFrom="md">
              <CardSkeletonLines count={3} sizes={["control", "md", "sm"]} gap={12} />
            </Paper>
          ))}
        </Stack>
      );
    }

    if (lines.length === 0) {
      if (lastError) {
        return null;
      }

      if (hasQueryNarrowing) {
        return (
          <Text size="md" c="dimmed" ta="center" p="md">
            Nothing here matches these filters.
          </Text>
        );
      }

      return (
        <Paper withBorder p="xl" radius="md">
          <EmptyState title="Nothing yet" description="Each document received, approved or sent back is recorded here, with who and when." iconName="History" size="md" />
        </Paper>
      );
    }

    const more = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={loadMore} />;

    if (isDesktop) {
      return (
        <Stack gap="md">
          <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              {header}
              <Table.Tbody>
                {lines.map((line) => (
                  <Table.Tr key={line.id}>
                    <Table.Td style={{ verticalAlign: "top" }}>
                      <TableDateTime value={line.happenedAt} />
                    </Table.Td>
                    <Table.Td style={{ verticalAlign: "top" }}>
                      <WhatCell line={line} showVendor={showVendor} />
                    </Table.Td>
                    <Table.Td style={{ verticalAlign: "top" }}>
                      <Text size="md" style={{ overflowWrap: "anywhere" }}>
                        {vendorHistoryWho(line)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {more}
        </Stack>
      );
    }

    return (
      <Stack gap="sm">
        {lines.map((line) => (
          <Paper key={line.id} withBorder radius="md" p="md">
            <Stack gap="sm">
              <WhatCell line={line} showVendor={showVendor} />
              <Text size="md" style={{ overflowWrap: "anywhere" }}>
                {vendorHistoryWho(line)}
              </Text>
              <TableDateTime value={line.happenedAt} inline />
            </Stack>
          </Paper>
        ))}
        {more}
      </Stack>
    );
  };

  return (
    <Stack gap="md">
      {filters}
      <ErrorAlert error={lastError} title="Couldn't read the history" page="Vendor history" operation="eventVendorHistoryList" onDismiss={clearError} />
      <ContentLoadingOverlay loading={loading && isLoaded}>
        {body()}
      </ContentLoadingOverlay>
    </Stack>
  );
};

export const VendorHistoryPanel = React.memo(VendorHistoryPanelComponent);
